import React, { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import IconClose from '@assets/images/close.svg';
import IconFilter from '@assets/images/icon_filter.svg';
import IconPinned from '@assets/images/icon_pinned.svg';
import IconUnpinned from '@assets/images/icon_unpinned.svg';
import {
  ApplyButton,
  FilterApplyArea,
  FilterArea,
  FilterContentArea,
  FilterTitleArea,
  ResetButton,
} from '@components/filter/FilterStyle';
import useWindow from '@lib/hooks/useWindow';
import { useSidePanelStore } from '@store/sidePanelStore';
import { breakpoints, media } from '@styles/mediaQuery';
import theme from '@theme/index';
import throttle from 'lodash/throttle';
import styled from 'styled-components';

/**
 * PageWithFilter - 통합 필터 시스템을 사용하는 페이지 레이아웃
 *
 * PageContainer를 대체하며, 페이지 타이틀과 필터 시스템을 통합 제공
 *
 * @param title - 페이지 타이틀
 * @param itemsRightSideTitle - 타이틀 우측 요소들
 * @param children - 메인 컨텐츠 영역
 * @param filters - 필터 아이템 배열 (FilterItem 컴포넌트들)
 * @param onApplyFilter - 필터 적용 시 실행할 함수
 * @param onResetFilter - 필터 초기화 시 실행할 함수
 * @param filterWidth - 필터 영역 너비 (기본: 14rem)
 * @param refetchOnFilterChange - 필터 값 변경 시 자동으로 refetch 실행 여부 (기본: false)
 * @param filterDependencies - 필터 변경을 감지하기 위한 의존성 배열 (refetchOnFilterChange가 true일 때만 사용)
 * @param throttleMs - throttle 주기 (밀리초, 기본: 1000ms)
 */

interface Props {
  title?: string;
  itemsRightSideTitle?: React.ReactElement[];
  children: ReactNode;
  filters?: ReactNode[];
  onApplyFilter?: () => void;
  onResetFilter?: () => void;
  filterWidth?: string;
  refetchOnFilterChange?: boolean;
  filterDependencies?: any[];
  throttleMs?: number;
}

const PageWithFilter = ({
  title,
  itemsRightSideTitle,
  children,
  filters,
  onApplyFilter,
  onResetFilter,
  filterWidth,
  refetchOnFilterChange = false,
  filterDependencies,
  throttleMs = 1000,
}: Props) => {
  const { pinned, isPanelOpen, setPinned, setIsPanelOpen } = useSidePanelStore();
  const windowSize = useWindow();

  // 최신 onApplyFilter를 참조하기 위한 ref
  const onApplyFilterRef = useRef(onApplyFilter);

  // onApplyFilter가 변경될 때마다 ref 업데이트
  useEffect(() => {
    onApplyFilterRef.current = onApplyFilter;
  }, [onApplyFilter]);

  // Throttle된 필터 적용 함수 (1초마다 최대 1번)
  const throttledApplyRef = useRef(
    throttle(() => {
      onApplyFilterRef.current?.();
    }, throttleMs),
  );

  const isDesktop = useMemo(() => {
    // actualWidth는 window.innerWidth (스크롤바 포함)
    // width는 document.body.clientWidth (스크롤바 제외)
    // 정확한 viewport 기준을 위해 actualWidth 사용
    const viewportWidth = windowSize?.actualWidth || windowSize?.width;

    // 초기 로딩 시(windowSize undefined) 모바일로 간주하여 패널을 숨김 (깜빡임 방지)
    if (!windowSize || !viewportWidth) return false;

    if (viewportWidth >= breakpoints.filterDesktop + 1) return true;
    return false;
  }, [windowSize]);

  const viewportWidth = windowSize?.actualWidth || windowSize?.width || 0;

  const isMobile = useMemo(() => {
    const viewportWidth = windowSize?.actualWidth || windowSize?.width;
    if (!windowSize || !viewportWidth || viewportWidth <= breakpoints.mobile) return true;
    return false;
  }, [windowSize]);

  const togglePinned = () => setPinned(!pinned);

  const toggleFilterEnabled = useCallback(() => {
    setIsPanelOpen(!isPanelOpen);
  }, [isPanelOpen, setIsPanelOpen]);

  const handleApplyFilter = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onApplyFilter?.();

    if (!isDesktop) {
      setIsPanelOpen(false);
    } else if (!pinned) {
      setIsPanelOpen(false);
    }
  };

  const handleResetFilter = () => {
    const filterResetEvent = new CustomEvent('reset-filter');
    window.dispatchEvent(filterResetEvent);
    onResetFilter?.();
  };

  // 필터 값 변경 시 throttle 적용하여 자동 refetch
  useEffect(() => {
    if (!refetchOnFilterChange) return;
    if (!filterDependencies || filterDependencies.length === 0) return;
    throttledApplyRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchOnFilterChange, ...(filterDependencies || [])]);

  if (!filters || filters.length === 0) {
    // 필터가 없으면 PageContainer 역할만 수행
    return (
      <PageContent>
        {(title || itemsRightSideTitle) && (
          <PageTitleArea>
            {title && <h1>{title}</h1>}
            {itemsRightSideTitle && (
              <RightSideTitleArea>
                {itemsRightSideTitle.map((item, index) =>
                  React.cloneElement(item, { key: `page-right-side-${index}` }),
                )}
              </RightSideTitleArea>
            )}
          </PageTitleArea>
        )}
        {children}
      </PageContent>
    );
  }

  return (
    <PageLayoutContainer>
      <MainContentArea pinned={pinned} enabled={isPanelOpen}>
        <PageContent>
          {(title || itemsRightSideTitle) && (
            <PageTitleArea>
              {title && <h1>{title}</h1>}
              <RightSideTitleArea>
                {/* 필터 토글 버튼 (모바일/태블릿 - 타이틀 영역) */}
                {!isDesktop && (
                  <FilterToggleButtonDesktop onClick={toggleFilterEnabled} enabled={isPanelOpen}>
                    <img src={IconFilter} alt="filter" />
                    <span>필터</span>
                  </FilterToggleButtonDesktop>
                )}
                {itemsRightSideTitle &&
                  itemsRightSideTitle.map((item, index) =>
                    React.cloneElement(item, { key: `page-right-side-${index}` }),
                  )}
              </RightSideTitleArea>
            </PageTitleArea>
          )}
          {children}
        </PageContent>
      </MainContentArea>

      {/* 필터 영역 */}
      <FilterArea
        pinned={pinned}
        enabled={isPanelOpen}
        fixed={isDesktop}
        filterWidth={filterWidth}
        onSubmit={handleApplyFilter}>
        {/* 필터 헤더 */}
        <FilterTitle
          pinned={pinned}
          fixed={isDesktop}
          isMobile={isMobile}
          viewportWidth={viewportWidth}
          toggleFilter={toggleFilterEnabled}
          togglePinned={togglePinned}
        />
        {/* 필터 컨텐츠 */}
        <FilterContentArea>
          {filters.map((filter, idx) => (
            <div key={idx}>{filter}</div>
          ))}
        </FilterContentArea>
        {/* 필터 적용/초기화 버튼 */}
        <FilterApplyArea>
          <ResetButton type="button" onClick={handleResetFilter}>
            초기화
          </ResetButton>
          <ApplyButton type="submit">적용</ApplyButton>
        </FilterApplyArea>
      </FilterArea>
    </PageLayoutContainer>
  );
};

export default PageWithFilter;

interface FilterTitleProps {
  fixed: boolean;
  pinned: boolean;
  isMobile: boolean;
  viewportWidth: number;
  toggleFilter: () => void;
  togglePinned: () => void;
}

const FilterTitle = ({
  fixed,
  pinned,
  isMobile,
  viewportWidth,
  toggleFilter,
  togglePinned,
}: FilterTitleProps) => {
  // 데스크톱(fixed)이거나 모바일/태블릿(<=768px)에서는 핀 버튼 숨김
  // breakpoints.tablet = 768.
  const showPinButton = !fixed && viewportWidth > breakpoints.tablet;

  // 데스크톱(fixed)에서는 닫기 버튼 숨김
  const showCloseButton = !fixed;

  return (
    <FilterTitleArea>
      {showPinButton ? (
        <PinButton onClick={togglePinned}>
          <img src={pinned ? IconPinned : IconUnpinned} alt="filter-pin" />
        </PinButton>
      ) : (
        <div />
      )}
      <span>필터</span>
      {showCloseButton ? (
        <CloseButton onClick={toggleFilter}>
          <img src={IconClose} alt="close" />
        </CloseButton>
      ) : (
        <div />
      )}
    </FilterTitleArea>
  );
};

// Styled Components

const PageLayoutContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const MainContentArea = styled.div<{ pinned: boolean; enabled: boolean }>`
  flex: 1;
  min-width: 0; /* flex 컨테이너 내부에서 overflow 방지 */

  /* 데스크톱 고정 상태: 필터 패널 너비만큼 우측 여백 확보 */
  @media (min-width: ${breakpoints.filterDesktop + 1}px) {
    width: 100%;
    padding-right: 20rem;
    transition: padding-right 0.25s ease-in-out;
  }

  /* 태블릿 이상 ~ 필터 데스크톱 미만: 핀 고정 + 활성화 상태일 때만 필터 패널 너비만큼 우측 여백 확보 */
  @media (min-width: ${breakpoints.tablet + 1}px) and (max-width: ${breakpoints.filterDesktop}px) {
    width: 100%;
    padding-right: ${({ pinned, enabled }) => (pinned && enabled ? '20rem' : '0')};
    transition: padding-right 0.25s ease-in-out;
  }
`;

const FilterToggleButtonDesktop = styled.button<{ enabled: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  background: ${({ enabled }) => (enabled ? theme.primary : theme.white)};
  color: ${({ enabled }) => (enabled ? theme.white : theme.neutralGray[700])};
  border: 1px solid ${({ enabled }) => (enabled ? theme.primary : theme.neutralGray[300])};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  img {
    width: 1rem;
    height: 1rem;
    /* enabled=true: 흰색, enabled=false: 회색(#666 정도) */
    filter: ${({ enabled }) =>
      enabled
        ? 'brightness(0) invert(1)'
        : 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(98%) contrast(90%)'};
  }

  &:hover {
    background: ${({ enabled }) => (enabled ? theme.main[500] : theme.neutralGray[100])};
    border-color: ${({ enabled }) => (enabled ? theme.main[500] : theme.neutralGray[400])};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;

    span {
      display: none;
    }
  }
`;

const PinButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 1.5rem;
    height: 1.5rem;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    opacity: 0.7;
  }
`;

// PageContainer 스타일 컴포넌트들
const PageContent = styled.div`
  width: 100%;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background: white;

  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.15);

  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0;
  }

  ${media.tablet`
    margin: 0;
    margin-bottom: 1.5rem;
    padding: 1rem;
    border-radius: 0;
  `}
`;

const PageTitleArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.tablet`
    gap: 1rem;
  `}
`;

const RightSideTitleArea = styled.div`
  display: flex;
  gap: 0.5rem;
`;
